import type { FC, MouseEvent } from 'react'
import { useCallback } from 'react'
import { useState } from 'react'
import { BiPlay } from 'react-icons/bi'
import { BsStars } from 'react-icons/bs'
import { Link, useNavigate } from 'react-router-dom'

import { Button } from '@app/components/ui/button'
import type { ButtonProps } from '@app/components/ui/button'
import { toaster } from '@app/components/ui/toaster'
import useStoreCurrentUser from '@app/hooks/useStoreCurrentUser'
import Can from '@app/shared/authorization/can'
import { usePlaybookApplyWithAiMutation } from '@graphql/queries'
import type { Playbook } from '@graphql/queries'

interface Props extends ButtonProps {
  playbook?: Pick<Playbook, 'id' | 'name'>
  businessContextId: string
  ctaText?: string
  additionalInstructions?: string
  onLoadingChange?: (loading: boolean) => void
}

const BuildWithAiButton: FC<Props> = ({
  playbook,
  businessContextId,
  ctaText = 'Build with AI',
  additionalInstructions,
  onLoadingChange = null,
  ...rest
}) => {
  const { id: playbookId } = playbook || {}
  const { user } = useStoreCurrentUser()
  const [, createStrategyWithAi] = usePlaybookApplyWithAiMutation()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)

  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      // Prevent the parent overlay (ImageWithOverlay) link from firing when this button is clicked.
      e.preventDefault()
      setLoading(true)
      onLoadingChange?.(true)

      createStrategyWithAi({
        input: {
          playbookId,
          additionalInstructions,
          businessContextId
        }
      })
        .then((resp) => {
          if (resp.error) {
            throw resp
          }

          const { errors } = resp.data.playbookApplyWithAi

          if (errors.length) {
            throw errors
          }

          toaster.create({
            title: 'Creating strategy map, hold tight!',
            type: 'success'
          })

          const { strategy, chat } = resp.data.playbookApplyWithAi
          let redirectUrl = `/strategy/${strategy.id}/map`

          if (chat) {
            redirectUrl = `${redirectUrl}/ai/${chat.id}`
          }

          navigate(redirectUrl)
        })
        .catch(() => {
          // we remain loading for the success case, since we are navigating away
          setLoading(false)
          onLoadingChange?.(false)
        })
    },
    [onLoadingChange, createStrategyWithAi, playbookId, additionalInstructions, businessContextId, navigate]
  )

  if (!user) {
    return (
      <Button key="sign_up" aria-label="Sign up to build with AI" {...rest} asChild>
        <Link to={`/sign_up?utm_source=playbooks&utm_medium=public&utm_content=ai_builder&utm_campaign=${playbookId}`}>
          <BiPlay /> Build with AI
        </Link>
      </Button>
    )
  }

  return (
    <Can I="use" a="playbook">
      <Button
        colorPalette="purple"
        loading={loading}
        onClick={handleClick}
        data-playbook-name={playbook?.name}
        data-playbook-id={playbookId}
        {...rest}
      >
        <BsStars />
        {ctaText}
      </Button>
    </Can>
  )
}

export default BuildWithAiButton
